import React, { useMemo } from 'react';
import { Box } from '@mui/material';
import {
  LocalShipping,
  Business,
  Group,
  Notifications,
  NoteAdd,
  Task,
  Timeline,
  Inventory2,
  ShoppingCart,
  Engineering,
  ReceiptLong,
  Category,
  PrecisionManufacturing,
  ViewInAr,
  Sell,
  PlaylistAddCheck,
} from '@mui/icons-material';
import PageLayout from '@/components/common/PageLayout';
import { useDocumentTitle } from '@/hooks/useAppConfig';
import { DashboardSection } from '@/components';
import { useDashboard, usePermissions } from '@/hooks';
import type { DashboardStatKey } from '@/hooks/useDashboard';
import type { DashboardStatsCardProps } from '@/components/dashboards';

interface LogisticsObjectItem {
  statKey: DashboardStatKey;
  title: string;
  to: string;
  icon: React.ReactNode;
  value?: number;
}

const DashboardPage: React.FC = () => {
  useDocumentTitle('Dashboard');
  const { can } = usePermissions();

  const {
    stats,
    statErrors,
    bolLogisticsLoading,
    bolManagementLoading,
    notificationsLoading,
  } = useDashboard({
    canReadHost: can.readHost,
    canReadMember: can.readMember,
    canReadLogisticsObject: can.readLogisticsObject,
    canReadSubscription: can.readSubscription,
  });

  const logisticsObjects = useMemo<LogisticsObjectItem[]>(
    () => [
      { statKey: 'quotationRequests', title: 'Quotation Requests', to: '/quotation-requests', icon: <NoteAdd />, value: stats.quotationRequests },
      { statKey: 'quotations', title: 'Quotations', to: '/quotations', icon: <Task />, value: stats.quotations },
      { statKey: 'services', title: 'Services', to: '/services', icon: <LocalShipping />, value: stats.services },
      { statKey: 'activities', title: 'Activities', to: '/activities', icon: <Timeline />, value: stats.activities },
      { statKey: 'consignments', title: 'Consignments', to: '/consignments', icon: <Inventory2 />, value: stats.consignments },
      { statKey: 'customerOrders', title: 'Customer Orders', to: '/customer-orders', icon: <ShoppingCart />, value: stats.customerOrders },
      { statKey: 'humanMeans', title: 'Human Means', to: '/human-means', icon: <Engineering />, value: stats.humanMeans },
      { statKey: 'invoicingDocuments', title: 'Invoicing Documents', to: '/invoicing-documents', icon: <ReceiptLong />, value: stats.invoicingDocuments },
      { statKey: 'items', title: 'Items', to: '/items', icon: <Category />, value: stats.items },
      { statKey: 'materialMeans', title: 'Material Means', to: '/material-means', icon: <PrecisionManufacturing />, value: stats.materialMeans },
      { statKey: 'pieces', title: 'Pieces', to: '/pieces', icon: <ViewInAr />, value: stats.pieces },
      { statKey: 'products', title: 'Products', to: '/products', icon: <Sell />, value: stats.products },
      { statKey: 'serviceRequests', title: 'Service Requests', to: '/service-requests', icon: <PlaylistAddCheck />, value: stats.serviceRequests },
    ],
    [
      stats.activities,
      stats.consignments,
      stats.customerOrders,
      stats.humanMeans,
      stats.invoicingDocuments,
      stats.items,
      stats.materialMeans,
      stats.pieces,
      stats.products,
      stats.quotationRequests,
      stats.quotations,
      stats.serviceRequests,
      stats.services,
    ]
  );

  const logisticsCards: Omit<DashboardStatsCardProps, 'loading'>[] = logisticsObjects.map(item => ({
    title: item.title,
    value: item.value ?? 0,
    icon: item.icon,
    color: 'primary' as const,
    to: item.to,
    ariaLabel: `Open ${item.title}`,
    error: statErrors[item.statKey],
  }));

  const managementCards: Omit<DashboardStatsCardProps, 'loading'>[] = [
    ...(can.readHost
      ? [
          {
            title: 'Hosts',
            value: stats.hosts,
            icon: <Business />,
            color: 'success' as const,
            to: '/hosts',
            ariaLabel: 'Open Hosts',
            error: statErrors.hosts,
          },
        ]
      : []),
    ...(can.readMember
      ? [
          {
            title: 'Members',
            value: stats.members,
            icon: <Group />,
            color: 'success' as const,
            to: '/members',
            ariaLabel: 'Open Members',
            error: statErrors.members,
          },
        ]
      : []),
  ];

  const notificationsCards: Omit<DashboardStatsCardProps, 'loading'>[] = [
    {
      title: 'Subscriptions',
      value: stats.subscriptions,
      icon: <Notifications />,
      color: 'secondary' as const,
      to: '/subscriptions',
      ariaLabel: 'Open Subscriptions',
      error: statErrors.subscriptions,
    },
    {
      title: 'Consumers',
      value: stats.totalConsumers,
      icon: <Group />,
      color: 'secondary',
      to: '/consumers',
      ariaLabel: 'Open Consumers',
      error: statErrors.totalConsumers,
    },
  ];

  return (
    <PageLayout title="Dashboard">
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', xl: '2fr 1fr' }, gap: 2 }}>
          {(can.readLogisticsObject || can.readHost || can.readMember) && (
            <DashboardSection
              title="BOL"
              icon={<Business />}
              color="primary"
              loading={bolLogisticsLoading || bolManagementLoading}
              error={null}
              errorMessage="BOL API not accessible"
              errorHelperText="Failed to load one or more BOL statistics. Contact your administrator if this persists."
              columns={{ xs: 1, sm: 2, md: 2, lg: 3 }}
              subsections={[
                ...(can.readLogisticsObject
                  ? [
                      {
                        title: 'Logistics Objects',
                        icon: <LocalShipping />,
                        color: 'primary' as const,
                        cards: logisticsCards,
                      },
                    ]
                  : []),
                ...(managementCards.length > 0
                  ? [
                      {
                        title: 'Management',
                        icon: <Group />,
                        color: 'success' as const,
                        cards: managementCards,
                      },
                    ]
                  : []),
              ]}
            />
          )}

          {can.readSubscription && (
            <DashboardSection
              title="BOL Notifications"
              icon={<Notifications />}
              color="secondary"
              loading={notificationsLoading}
              error={null}
              errorMessage="Notifications API not accessible"
              errorHelperText="You may not have the required permissions yet. Contact your administrator to access the BOL Notifications API."
              columns={{ xs: 1, sm: 1, md: 1, lg: 1 }}
              subsections={[
                {
                  title: 'Management',
                  icon: <Group />,
                  color: 'secondary' as const,
                  cards: notificationsCards,
                },
              ]}
            />
          )}
        </Box>
      </Box>
    </PageLayout>
  );
};

export default DashboardPage;
